import React, { useRef, useCallback, useMemo, useEffect } from "react";
import { useHomeV2Context } from "../context/home_v2_context";
import styles from "../styles/home_v2_style.module.css";
import {
  Video,
  MessageCircle,
  Shield,
  Clock,
  Heart,
  ArrowRight,
  Brain,
  Users,
  Play,
  Star,
  Server,
  Globe,
  Pause,
  VolumeX,
  Volume2,
} from "lucide-react";
import "../styles/animation.css";
import { useSelector } from "react-redux";
import FixedModal from "./FixedModal";
import StartExperienceModal from "../../../components/popups/start_experience_modal";
import TestimonialComp from "./testimonial_comp";
import FAQComp from "./faq_comp";
import EnhancedFeaturesComp from "./enhanced_features_comp";
import RelationshipTimelineComp from "./relationship_timeline_comp";
import HeroComp from "./hero_comp";
import FadeInSection from "./fade_in_section_comp";
import FinalCtaComp from "./final_cta_comp";
import { useNavigate } from "react-router-dom";

const HomeV2Comp = () => {
  const navigate = useNavigate();

  const {
    currentView,
    isPlaying,
    setIsPlaying,
    isMuted,
    progress,
    showControls,
    setShowControls,
    selectedMode,
    closeVideoModal,
    handleModeSelection,
    togglePlay,
    toggleMute,
    updateProgress,
    resetControlsTimeout,
    videoModalOpen,
    setVideoModalOpen,
    privacyModalOpen,
    setPrivacyModalOpen,
    termsModalOpen,
    setTermsModalOpen,
    contactModalOpen,
    setContactModalOpen,
    activeCompanion,
    companions,
    videoRef,
    handleVideoLoaded,
    videoSrc,
    videoPoster,
    startExperienceModalOpen,
    setStartExperienceModalOpen,
  } = useHomeV2Context();

  const user = useSelector((state) => state.user.user);

  const VideoModal = useMemo(
    () => (
      <FixedModal
        isOpen={videoModalOpen}
        onClose={() => {
          closeVideoModal();
          setVideoModalOpen(false);
        }}
        title={null}
      >
        <div
          key={`modal-header-${activeCompanion}`}
          className="bg-gradient-to-r from-red-800 via-red-900 to-red-950 p-6 text-center relative z-10 shadow-md max-sm:p-2"
        >
          <h2 className="text-3xl font-bold">
            Meet <span className={`italic font-serif ${styles.textShimmer} text-3xl pr-1`}>{companions[activeCompanion].name}</span>
          </h2>
          <p className="text-red-200 mt-1">A Relationship That Evolves</p>
        </div>

        {currentView === "modeSelection" && (
          <div className="relative p-6">
            <h2 className="text-2xl font-bold text-center text-red-200 mb-6">Choose Your Experience</h2>

            <div className="flex flex-col items-center justify-center py-4">
              <p key={`modal-selection-text-${activeCompanion}`} className="text-lg text-red-300 mb-8 text-center max-w-lg">
                Select how you would like to connect with {companions[activeCompanion].name}
              </p>

              <div className="flex sm:flex-row gap-6 mb-8 justify-center">
                <div
                  className={`md:w-64 max-sm:pt-4 max-sm:px-3 p-6 bg-gradient-to-b from-slate-900/80 via-red-950/30 to-slate-900/80 rounded-xl border border-red-900/30 flex flex-col items-center cursor-pointer hover:border-red-500/50 transition-all duration-300 hover:translate-y-[-5px] hover:shadow-lg hover:shadow-red-900/20 ${styles.modeCard}`}
                  onClick={() => handleModeSelection("companion")}
                >
                  <div className="w-16 h-16 rounded-full bg-slate-900/60 flex items-center justify-center mb-4">
                    <Users size={28} className="text-red-300" />
                  </div>
                  <h3 className="max-sm:text-base text-xl font-semibold mb-2 text-white">Companion Mode</h3>
                  <p className="text-center text-red-300 text-sm">Friendly conversation and emotional connection</p>
                </div>

                <div
                  className={`md:w-64 max-sm:pt-4 max-sm:px-3 p-6 bg-gradient-to-b from-slate-900/80 via-red-950/50 to-slate-900/80 rounded-xl border border-red-900/30 flex flex-col items-center cursor-pointer hover:border-red-500/50 transition-all duration-300 hover:translate-y-[-5px] hover:shadow-lg hover:shadow-red-900/20 ${styles.modeCard}`}
                  onClick={() => handleModeSelection("erotix")}
                >
                  <div className="w-16 h-16 rounded-full bg-slate-900/60 flex items-center justify-center mb-4">
                    <Heart
                      size={28}
                      className="text-red-500"
                      style={{
                        filter: "drop-shadow(0 0 6px rgba(220, 38, 38, 0.6))",
                      }}
                    />
                  </div>
                  <h3 className="max-sm:text-base text-xl font-semibold mb-2 text-white">ErotiX Mode</h3>
                  <p className="text-center text-red-300 text-sm">Explicit video with full nudity and adult content</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentView === "videoPlayer" && (
          <div className="relative p-6 pb-12">
            <div
              className="relative aspect-video bg-black/80 rounded-lg  mb-6"
              onMouseMove={resetControlsTimeout}
              onMouseLeave={() => {
                if (isPlaying) {
                  setShowControls(false);
                }
              }}
            >
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                onTimeUpdate={updateProgress}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onLoadedData={handleVideoLoaded}
                muted={isMuted}
                onClick={togglePlay}
                preload="auto"
                autoPlay
                playsInline
                loop
              >
                <source src={companions[activeCompanion].videoSpeaking} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <div
                className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 ${styles.videoControls} ${
                  showControls ? "" : styles.videoControlsHidden
                }`}
              >
                <div
                  className="w-full h-1 bg-gray-700/50 rounded-full mb-4 cursor-pointer"
                  onClick={(e) => {
                    if (videoRef.current) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const position = (e.clientX - rect.left) / rect.width;
                      videoRef.current.currentTime = position * videoRef.current.duration;
                    }
                  }}
                >
                  <div className={styles.progressBar} style={{ width: `${progress}%` }}></div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button
                      className="w-10 h-10 rounded-full bg-red-900/30 flex items-center justify-center hover:bg-red-900/50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePlay();
                      }}
                    >
                      {isPlaying ? <Pause size={18} className="text-white" /> : <Play size={18} className="text-white" />}
                    </button>

                    <button
                      className="w-10 h-10 rounded-full bg-red-900/30 flex items-center justify-center hover:bg-red-900/50 transition-colors"
                      onClick={toggleMute}
                    >
                      {isMuted ? <VolumeX size={18} className="text-white" /> : <Volume2 size={18} className="text-white" />}
                    </button>

                    <div className="text-sm text-red-300">
                      {videoRef.current ? (
                        <>
                          {Math.floor(videoRef.current.currentTime / 60)}:{String(Math.floor(videoRef.current.currentTime % 60)).padStart(2, "0")} /
                          {Math.floor((videoRef.current.duration || 0) / 60)}:
                          {String(Math.floor((videoRef.current.duration || 0) % 60)).padStart(2, "0")}
                        </>
                      ) : (
                        "0:00 / 0:00"
                      )}
                    </div>
                  </div>

                  <div className="bg-red-900/40 px-2 py-0.5 rounded text-xs font-medium text-white">HD</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-950/40 border border-red-900/20 rounded-lg p-5 mb-4">
              <p key={`video-desc-${activeCompanion}`} className="text-red-300 text-sm">
                <span className="font-semibold text-lg block mb-1">Experience Continuous Memory</span>
                Unlike other AI companions that leave you unsatisfied, {companions[activeCompanion].name} remembers your deepest desires and secret
                fantasies. Watch how she recalls your most intimate moments, creating an increasingly seductive and passionate affair that keeps you
                coming back for more.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mb-6 max-sm:hidden">
              {[
                "Remembers your preferences and past conversations",
                "Responds to emotional cues with authentic expressions",
                "Develops a deeper connection as you spend time together",
                "Adapts her personality based on your interactions",
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-1.5">
                  <div className="w-5 h-5 rounded-full bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <Heart size={12} className="text-red-400" />
                  </div>
                  <span className="text-red-200 text-xs">{feature}</span>
                </div>
              ))}
            </div>

            <div className="text-center mt-6">
              <button
                className="px-8 py-3 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-full text-lg font-medium hover:shadow-lg hover:shadow-red-700/30 hover:brightness-110 transition-all duration-300 relative group active:translate-y-0.5 active:shadow-none"
                onClick={() => {
                  if (videoRef.current) {
                    videoRef.current.pause(); // Pause the video before navigating
                  }
                  navigate("/sign-up"); // Navigate to the /sign-up page
                }}
              >
                <span key={`cta-button-text-${activeCompanion}`} className="flex items-center justify-center">
                  <span className="text-white">Experience {companions[activeCompanion].name} Tonight</span>
                  <ArrowRight className="ml-2 w-5 h-5" />
                </span>
              </button>
              <p className="mt-2 text-red-400/70 text-sm">No credit card required</p>
            </div>
          </div>
        )}
      </FixedModal>
    ),
    [videoModalOpen, currentView, activeCompanion, companions, selectedMode, isPlaying, progress, isMuted, showControls]
  );

  const ContactModal = useMemo(
    () => (
      <FixedModal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} title="Contact Us">
        <div className="p-6">
          <div className="mb-8">
            <form className="space-y-4">
              <div>
                <label className="block text-red-200 mb-2">Name</label>
                <input
                  type="text"
                  className="w-full bg-slate-900/70 border border-red-900/30 rounded-lg p-3 text-white focus:outline-none focus:ring-2 focus:ring-red-600/50"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-red-200 mb-2">Email</label>
                <input
                  type="email"
                  className="w-full bg-slate-900/70 border border-red-900/30 rounded-lg p-3 text-white focus:outline-none focus:ring-2 focus:ring-red-600/50"
                  placeholder="Your email"
                />
              </div>
              <div>
                <label className="block text-red-200 mb-2">Message</label>
                <textarea
                  className="w-full bg-slate-900/70 border border-red-900/30 rounded-lg p-3 text-white focus:outline-none focus:ring-2 focus:ring-red-600/50 min-h-[100px]"
                  placeholder="Your message"
                ></textarea>
              </div>
              <button
                type="button"
                className="w-full py-3 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-lg font-medium hover:brightness-110 transition-all"
              >
                Send Message
              </button>
            </form>
          </div>
          <div className="text-center text-red-300">
            <p>Or reach us directly at:</p>
            <p className="font-medium mt-2">support@erotixai.com</p>
          </div>
        </div>
      </FixedModal>
    ),
    [contactModalOpen]
  );

  return (
    <div className="min-h-screen text-white relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-900/5 rounded-full blur-3xl animate-ambient-pulse"></div>
        <div
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-rose-900/5 rounded-full blur-3xl animate-ambient-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
        <div
          className="absolute top-1/2 right-1/3 w-64 h-64 bg-fuchsia-900/5 rounded-full blur-3xl animate-ambient-pulse"
          style={{ animationDelay: "4s" }}
        ></div>
      </div>

      <div className="relative flex flex-col items-center px-4 pb-8 ">
        <FadeInSection direction="downToUp">
          <HeroComp />
        </FadeInSection>

        <FadeInSection direction="leftToRight">
          <EnhancedFeaturesComp />
        </FadeInSection>

        <FadeInSection direction="leftToRight">
          <RelationshipTimelineComp />
        </FadeInSection>
        <div className="w-full max-w-4xl mx-auto pt-8 scroll-mt-20">
          <FadeInSection direction="downToUp">
            <TestimonialComp />
          </FadeInSection>
        </div>

        <FadeInSection direction="leftToRight">
          <FAQComp />
        </FadeInSection>

        <FadeInSection direction="downToUp">
          <FinalCtaComp />
        </FadeInSection>
      </div>

      {startExperienceModalOpen && <StartExperienceModal handleClose={() => setStartExperienceModalOpen(false)} />}

      {VideoModal}
      {ContactModal}
    </div>
  );
};

export default HomeV2Comp;
